<script>
import PhoenixForm from '@phoenix/mixins/components/form/PhoenixForm';
import { PhxIcon } from '@phx-styleguides/als';
import { useForm } from 'vee-validate';
import { defineAsyncComponent } from 'vue';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import { PhxsgLoader } from '@sg/src/View/Components/Atoms/Loader';
import { PhxsgDivider } from '@sg/src/View/Components/Atoms/Divider';

export default {
  components: {
    ChinesePIPL: defineAsyncComponent(() => import('@maison/components/form/fields/ChinesePIPL')),
    KoreanGDPR: defineAsyncComponent(() => import('@maison/components/form/KoreanGDPR')),
    PhxIcon,
    PhxsgButton,
    PhxsgLoader,
    PhxsgDivider,
  },

  mixins: [PhoenixForm],

  provide() {
    return {
      formType: this.formType,
    };
  },

  props: {
    customSubmitButtonClasses: {
      type: String,
      default: null,
    },
    centeredActions: {
      type: Boolean,
      default: false,
    },
    hasFormActionsMarginTop: {
      type: Boolean,
      default: true,
    },
    overrideFormActionClasses: {
      type: String,
      default: '',
    },
  },

  setup() {
    const { setValues, validate } = useForm();
    return { setValues, validate };
  },

  computed: {
    shouldDisplayNewsletter() {
      return !this.shouldDisplayKoreanGDPR && getFeatureFlag(FEATURE_FLAG.SWSE_NEWSLETTER);
    },

    submitButtonClasses() {
      return this.customSubmitButtonClasses;
    },

    termsLinkClasses() {
      return ['form-actions__terms'];
    },

    formActionsClasses() {
      if (this.overrideFormActionClasses) {
        return this.overrideFormActionClasses;
      }

      return [
        'form-actions',
        {
          'form-actions--start': !this.centeredActions,
          'mt-md': this.hasFormActionsMarginTop,
        },
      ];
    },

    requiredFieldsText() {
      return this.$t('Form.required_field');
    },
  },

  methods: { getFeatureFlag },
};
</script>

<template>
  <form ref="form" style="position: relative" @submit.prevent="submit">
    <phxsg-divider v-if="shouldDisplayRequiredText" wrapped spacing="md" class="lg:phxsg-divider--lg">
      {{ requiredFieldsText }}<abbr class="text-ruby">*</abbr>
    </phxsg-divider>

    <div v-if="isLoading" class="flex flex-grid--center">
      <phxsg-loader size="lg" show-overlay />
    </div>

    <slot :should-display-newsletter="shouldDisplayNewsletter" />

    <korean-g-d-p-r
      v-if="shouldDisplayKoreanGDPR"
      :should-handle-newsletter="shouldHandleKoreanGDPRNewsletter"
      :initial-values="formValues"
      :form-type="koreanFormType"
      @change="onKoreanGDPRChange"
      @consent-values-loaded="onConsentValuesLoaded"
    />

    <chinese-p-i-p-l
      v-if="shouldDisplayPIPLConsentCheckboxes"
      :value="chinesePiplConsents"
      :custom-inner-class="customConsentInnerClass"
      :custom-outer-class="customConsentOuterClass"
      :form-type="formType"
      @on-chinese-pipl-consent-change="onChinesePiplConsentChange"
    />

    <div v-if="!showErrorsInToast && mainErrors" class="alert alert--error mt-base" role="alert">
      {{ mainErrors }}
    </div>

    <div v-if="shouldShowFormActions" :class="formActionsClasses">
      <slot
        name="form-actions"
        :is-disabled="isDisabled"
        :submit-button-classes="submitButtonClasses"
        :submit-text="submitText"
      />

      <phxsg-button
        v-if="shouldDisplaySubmitButton"
        ref="submitButton"
        :disabled="isDisabled"
        variant="primary"
        :class="submitButtonClasses"
        type="submit"
        data-cy="form-submit-btn"
      >
        {{ submitText }}
      </phxsg-button>

      <phxsg-button v-if="shouldDisplayPreviousButton" @click.prevent="goToPreviousStep">
        {{ previousStepText }}
      </phxsg-button>

      <phxsg-button
        v-if="shouldDisplayCancelButton"
        ref="cancelButton"
        :disabled="isDisabled"
        variant="secondary"
        data-cy="form-cancel-btn"
        @click.prevent="onCancelCallback"
      >
        {{ cancelText }}
      </phxsg-button>

      <div v-if="shouldDisplayTermsLink" :class="termsLinkClasses">
        <div class="flex gap-sm items-center">
          <phx-icon icon="info" style="min-width: 20px" />
          <p v-html="termsLink" />
        </div>
      </div>
    </div>
  </form>
</template>
